import { type ReactNode, useEffect } from 'react';

import { applyDssBodyClass } from './dss/dss';
import { PageLoading } from './PageLoading';
import { applyFavicon } from './skin/apply-favicon';
import { applySkinBodyClass } from './skin/skin-body-class';
import { useShellSkin } from './skin/useShellSkin';

export interface ShellProps {
  readonly children: ReactNode;
}

/**
 * Moldura nula para telas autenticadas que já trazem o próprio enquadramento
 * (logo, card, título) — o wizard de primeiro acesso é o caso. Não renderiza
 * chrome algum, como o ChromelessShell, mas aplica no body o contexto de estilo
 * que a folha Angular espera e repõe o overlay de carregamento que ela aciona.
 *
 * Essa é a diferença que justifica o shell: as regras do novoLayout vivem sob
 * `.not-dss-style .novoLayout` (_novolayout.scss), então sem a classe no body o
 * `@import "novolayout/button"` não alcança a folha e os botões caem no estilo
 * base do Bootstrap (sem border-radius, altura e fonte próprias). O
 * ChromelessShell omite as duas classes de propósito, porque atende tela pública
 * acessada sem sessão — o que não é o caso aqui.
 */
export function BareShell({ children }: ShellProps): JSX.Element {
  const { cor, faviconRaw } = useShellSkin();

  useEffect(() => {
    applyFavicon(faviconRaw);
  }, [faviconRaw]);

  // Chrome e DSS são mutuamente exclusivos: só o DssShell aplica classes dss-style-*.
  useEffect(() => {
    applyDssBodyClass(false, null);
    return () => {
      document.body.classList.remove('not-dss-style');
    };
  }, []);

  // A folha Angular consome as variáveis --default-color* das classes body-<cor>
  // (_theme-colors.scss). Sem elas, botões como .btn-pri1 perdem a cor da conta.
  useEffect(() => {
    applySkinBodyClass(cor);
  }, [cor]);

  // Sem coluna de conteúdo para ancorar, o overlay cobre a viewport, como o
  // `.pageloadingWizard` do FullLayout.
  return (
    <>
      {children}
      <PageLoading />
    </>
  );
}
